import { CSSProperties, FC, ReactNode, useLayoutEffect, useState } from 'react';

import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';

import { Button } from '../Button';
import { Close } from '../Icons';

import styles from './toast.module.scss';

interface IToastProps {
  children: ReactNode;
  tone?: 'danger' | 'success' | 'neutral';
  onDismiss?: () => void;
  style?: CSSProperties;
}

let viewport: HTMLDivElement | null = null;
let ownerCount = 0;

const acquireViewport = (): HTMLDivElement => {
  if (!viewport) {
    viewport = document.createElement('div');
    viewport.className = styles.viewport;
    viewport.setAttribute('data-overlay-announcement', '');
    document.body.appendChild(viewport);
  }
  ownerCount += 1;
  return viewport;
};

const releaseViewport = (): void => {
  ownerCount -= 1;
  if (!ownerCount) {
    viewport?.remove();
    viewport = null;
  }
};

const Toast: FC<IToastProps> = ({ children, tone = 'danger', onDismiss, style }) => {
  const intl = useIntl();
  const [host, setHost] = useState<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    setHost(acquireViewport());
    return releaseViewport;
  }, []);
  if (!host) return null;
  return createPortal(
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={styles.toast}
      data-tone={tone}
      style={style}
    >
      <div className={styles.message}>{children}</div>
      {onDismiss && (
        <Button
          mode="tertiary"
          color="dark"
          icon={<Close />}
          aria-label={intl.formatMessage({ id: 'common.close' })}
          onClick={onDismiss}
        />
      )}
    </div>,
    host,
  );
};

export { Toast };
